import {
  type Action,
  FilterActionTypes,
  type Thunk,
  action,
  thunk,
} from "easy-peasy";
import set from "lodash/set";

import type { ByDate, ByRestaurantId, BySession } from "./types";
import { Logger } from "../dependencies/logger";
import { batcher } from "../dependencies/utils";
import {
  type FirebaseAllAvailabilitySlot,
  type FirebaseAvailabilityPathMode,
  type FirebaseModel,
  firebaseModel,
  replayCachedSessionSnapshots as replayCachedSessionSnapshotsFromFirebase,
} from "../firebase/allAvailability";
import type { NonEmptyArray } from "../types";
import { isSameAvailabilitySnapshot } from "../utils/discountAvailability";
import { type LogEffect, createLogEffect } from "../utils/store";

export type { FirebaseAvailabilityPathMode };

const logEffect = (
  property: keyof FilterActionTypes<FirebaseAllAvailabilityModel>,
) =>
  createLogEffect<FirebaseAllAvailabilityModel>(
    "FIREBASE_ALL_AVAILABILITY",
    property,
    false,
  );

const logger = new Logger(
  "RESTAURANT_STORE - FIREBASE_ALL_AVAILABILITY",
  "LOG",
);
// Hot-path merges/syncs — keep disabled unless debugging availability.
// logger.enable();
const VERBOSE = false;

export type FirebaseAllAvailabilityById = ByRestaurantId<
  BySession<ByDate<FirebaseAllAvailabilitySlot[] | null>>
>;

export type FirebaseAllAvailabilityModel = {
  firebase: FirebaseModel;

  byId: FirebaseAllAvailabilityById;
  byIdOnChange: LogEffect<FirebaseAllAvailabilityModel>;
  clearState: Action<FirebaseAllAvailabilityModel>;
  clear: Thunk<FirebaseAllAvailabilityModel>;

  merge: Action<
    FirebaseAllAvailabilityModel,
    {
      restaurantId: number;
      session: SessionType;
      date: string;
      availability: FirebaseAllAvailabilitySlot[] | null;
    }[]
  >;

  loading: boolean;
  loadingOperationCount: number;
  loadingOnChange: LogEffect<FirebaseAllAvailabilityModel>;
  startLoading: Action<FirebaseAllAvailabilityModel>;
  stopLoading: Action<FirebaseAllAvailabilityModel>;

  fetch: Thunk<
    FirebaseAllAvailabilityModel,
    {
      restaurantIds: number[];
      sessionsById: ByRestaurantId<SessionType[]>;
      datesById: ByRestaurantId<NonEmptyArray<string>>;
      onlyOnceById: ByRestaurantId<boolean>;
      pathMode: FirebaseAvailabilityPathMode;
      abortSignal: AbortSignal;
    }
  >;
  syncLiveSubscriptions: Thunk<
    FirebaseAllAvailabilityModel,
    {
      restaurantIds: number[];
      sessionsById: ByRestaurantId<SessionType[]>;
      datesById: ByRestaurantId<NonEmptyArray<string>>;
      pathMode: FirebaseAvailabilityPathMode;
      abortSignal: AbortSignal;
    }
  >;
  /**
   * Replays cached session snapshots when the week date window changes for
   * restaurants that are not currently live subscribed.
   */
  replayCachedSessionSnapshots: Thunk<
    FirebaseAllAvailabilityModel,
    {
      restaurantIds: number[];
      sessionsById: ByRestaurantId<SessionType[]>;
      datesById: ByRestaurantId<NonEmptyArray<string>>;
      abortSignal: AbortSignal;
    }
  >;
};

const BATCHER_PERIOD = 200;

/**
 * Per-store queue for live desired-set reconciliation only. Keyed by each
 * store's `unsubsByPath` object so nested restaurant stores never block each
 * other. onlyOnce fetch and cached replay intentionally bypass this queue.
 */
const liveSubscriptionSyncByStore = new WeakMap<object, Promise<void>>();

const enqueueLiveSubscriptionSync = (
  storeIdentity: object,
  run: () => Promise<void>,
): Promise<void> => {
  const previous =
    liveSubscriptionSyncByStore.get(storeIdentity) ?? Promise.resolve();
  const next = previous.then(run, run);
  liveSubscriptionSyncByStore.set(storeIdentity, next);
  return next;
};

type MergePayload = {
  restaurantId: number;
  session: SessionType;
  date: string;
  availability: FirebaseAllAvailabilitySlot[] | null;
};

const hasMissingAvailabilityData = (
  byId: FirebaseAllAvailabilityById,
  restaurantIds: number[],
  sessionsById: ByRestaurantId<SessionType[]>,
  datesById: ByRestaurantId<NonEmptyArray<string>>,
): boolean => {
  for (const restaurantId of restaurantIds) {
    const sessions = sessionsById[restaurantId] ?? [];
    const dates = datesById[restaurantId] ?? [];

    if (dates.length === 0) {
      continue;
    }

    for (const session of sessions) {
      for (const date of dates) {
        if (byId[restaurantId]?.[session]?.[date] === undefined) {
          return true;
        }
      }
    }
  }

  return false;
};

/**
 * Collects the initial subscribe burst into one immediate merge, then optionally
 * batches subsequent live onValue events. Avoids the 200ms batcher delay on
 * first paint / next-page onlyOnce loads.
 *
 * Same-ref skip here avoids batcher work; `merge` is the write barrier (see
 * {@link isSameAvailabilitySnapshot} for the full identity pipeline).
 */
const createAvailabilityValueHandlers = (
  getById: () => FirebaseAllAvailabilityById,
  merge: (items: MergePayload[]) => void,
  batchSubsequentLiveEvents: boolean,
) => {
  const pendingInitial: MergePayload[] = [];
  let collectingInitial = true;

  const flushToMerge = (items: MergePayload[]) => {
    if (items.length === 0) {
      return;
    }

    merge(items);
  };

  const liveBatcher = batchSubsequentLiveEvents
    ? batcher(flushToMerge, BATCHER_PERIOD)
    : null;

  const onValue = async (item: MergePayload) => {
    if (collectingInitial) {
      const current = getById()[item.restaurantId]?.[item.session]?.[item.date];
      // Re-subscribe for an id already in byId often returns the same
      // fingerprint-cached array. Clone so flushInitial/merge writes.
      if (current === item.availability && Array.isArray(item.availability)) {
        pendingInitial.push({
          ...item,
          availability: [...item.availability],
        });
        return;
      }
      pendingInitial.push(item);
      return;
    }

    const current = getById()[item.restaurantId]?.[item.session]?.[item.date];

    // Same snapshot ref: skip batch/timer; merge would no-op anyway.
    if (isSameAvailabilitySnapshot(current, item.availability)) {
      return;
    }

    if (liveBatcher) {
      await liveBatcher(item);
      return;
    }

    flushToMerge([item]);
  };

  const flushInitial = () => {
    collectingInitial = false;
    if (pendingInitial.length === 0) {
      return;
    }

    flushToMerge(pendingInitial.splice(0, pendingInitial.length));
  };

  return { onValue, flushInitial };
};

const beginLoadingOperation = (
  abortSignal: AbortSignal,
  startLoading: () => void,
  stopLoading: () => void,
): (() => void) => {
  let finished = false;

  const finish = () => {
    if (finished) {
      return;
    }

    finished = true;
    abortSignal.removeEventListener("abort", finish);
    stopLoading();
  };

  startLoading();
  abortSignal.addEventListener("abort", finish, { once: true });
  return finish;
};

const firebaseAllAvailabilityModel: FirebaseAllAvailabilityModel = {
  firebase: firebaseModel,

  byId: {},
  byIdOnChange: logEffect("byId"),
  // Process caches are cleared by firebase.unsubAll (called from clear).
  clearState: action((state) => {
    if (Object.keys(state.byId).length === 0) {
      return;
    }
    logger.log("Clear");
    state.byId = {};
  }),
  clear: thunk(async (actions) => {
    actions.firebase.unsubAll();
    actions.clearState();
  }),

  /**
   * Write barrier for availability slots. Same-ref → no Immer write (see
   * {@link isSameAvailabilitySnapshot}). Required for replay, which bypasses
   * the live onValue early-exit.
   */
  merge: action((state, payload) => {
    if (payload.length === 0) {
      return;
    }

    let didChange = false;

    for (const { restaurantId, session, date, availability } of payload) {
      const current = state.byId[restaurantId]?.[session]?.[date];

      if (isSameAvailabilitySnapshot(current, availability)) {
        continue;
      }

      if (VERBOSE) {
        logger.log("Set", {
          restaurantId,
          session,
          date,
          availability,
        });
      }

      set(state.byId, [restaurantId, session, date], availability);
      didChange = true;
    }

    if (!didChange) {
      return;
    }

    if (VERBOSE) {
      logger.log("Merge");
    }
  }),

  loading: false,
  loadingOperationCount: 0,
  loadingOnChange: logEffect("loading"),
  startLoading: action((state) => {
    state.loadingOperationCount += 1;
    state.loading = true;
    logger.log("Start loading", state.loadingOperationCount);
  }),
  stopLoading: action((state) => {
    state.loadingOperationCount = Math.max(0, state.loadingOperationCount - 1);
    state.loading = state.loadingOperationCount > 0;
    logger.log("Stop loading", state.loadingOperationCount);
  }),

  // onlyOnce does not mutate the live listener set — run outside the live queue
  // so next-page fetches are not blocked by in-view reconciliation.
  fetch: thunk(async (actions, payload, { getState }) => {
    if (payload.abortSignal.aborted) {
      return;
    }

    const count = payload.restaurantIds.length;

    if (VERBOSE) {
      logger.log("Fetching availability...", payload);
    } else {
      logger.log(`Fetching availability (${count})...`);
    }

    const finishLoading = beginLoadingOperation(
      payload.abortSignal,
      actions.startLoading,
      actions.stopLoading,
    );

    const { onValue, flushInitial } = createAvailabilityValueHandlers(
      () => getState().byId,
      (items) => actions.merge(items),
      false,
    );

    try {
      await actions.firebase.subscribe({
        restaurantIds: payload.restaurantIds,
        sessionsById: payload.sessionsById,
        datesById: payload.datesById,
        onlyOnceById: payload.onlyOnceById,
        pathMode: payload.pathMode,
        onValue,
        abortSignal: payload.abortSignal,
      });
      flushInitial();
    } finally {
      finishLoading();

      if (payload.abortSignal.aborted) {
        logger.log(`Fetch availability aborted (${count})`);
        return;
      }

      logger.log(`Fetch availability done (${count})`);
    }
  }),

  syncLiveSubscriptions: thunk(async (actions, payload, { getState }) => {
    // Unique per store instance; avoids a process-global queue across nested stores.
    const storeIdentity = getState().firebase.unsubsByPath;

    await enqueueLiveSubscriptionSync(storeIdentity, async () => {
      if (payload.abortSignal.aborted) {
        return;
      }

      const needsFetch = hasMissingAvailabilityData(
        getState().byId,
        payload.restaurantIds,
        payload.sessionsById,
        payload.datesById,
      );

      logger.log(
        `Syncing live availability subscriptions (${payload.restaurantIds.length}, ${payload.pathMode})...`,
      );

      const { onValue, flushInitial } = createAvailabilityValueHandlers(
        () => getState().byId,
        (items) => actions.merge(items),
        true,
      );

      const finishLoading = needsFetch
        ? beginLoadingOperation(
            payload.abortSignal,
            actions.startLoading,
            actions.stopLoading,
          )
        : () => undefined;

      try {
        await actions.firebase.subscribe({
          restaurantIds: payload.restaurantIds,
          sessionsById: payload.sessionsById,
          datesById: payload.datesById,
          onlyOnceById: {},
          pathMode: payload.pathMode,
          onValue,
          abortSignal: payload.abortSignal,
        });
        flushInitial();
      } finally {
        finishLoading();
      }

      if (payload.abortSignal.aborted) {
        logger.log("Live availability subscription sync aborted");
        return;
      }

      logger.log("Live availability subscription sync done");
    });
  }),

  // Cache replay only reads snapshots / merges; it must not wait on live sync.
  // Abort previous replays when the week date window changes so a stale run
  // cannot finish last and overwrite byId with an older window.
  replayCachedSessionSnapshots: thunk(
    async (actions, payload, { getState }) => {
      if (payload.abortSignal.aborted) {
        return;
      }

      const mergeItems: MergePayload[] = [];

      await replayCachedSessionSnapshotsFromFirebase(
        payload.restaurantIds,
        payload.sessionsById,
        payload.datesById,
        getState().firebase.unsubsByPath,
        (item) => {
          if (payload.abortSignal.aborted) {
            return Promise.resolve();
          }

          mergeItems.push(item);
          return Promise.resolve();
        },
      );

      if (payload.abortSignal.aborted || mergeItems.length === 0) {
        return;
      }

      actions.merge(mergeItems);
    },
  ),
};

export default firebaseAllAvailabilityModel;
